import React from 'react';
import {
  AppButton,
  Box,
  FormProvider,
  Preloader,
  Text,
} from '@common-fe/common-fe';

import ModalWrapper from '@/components/wrappers/ModalWrapper';

import { AuthorizedUser, CreateAuthorizedUserParams, UpdateAuthorizedUserParams } from '../../authorizedUsers.types';

import useAuthorizedUserForm, { AuthorizedUserFormValues } from './hooks/useAuthorizedUserForm';
import AuthorizedUsersForm from './AuthorizedUsersForm';

interface Props {
  editingUser: AuthorizedUser | null;
  isSubmitting: boolean;
  onCreate: (params: CreateAuthorizedUserParams) => void;
  onUpdate: (params: UpdateAuthorizedUserParams) => void;
  onClose: () => void;
}

const AuthorizedUsersModal: React.FC<Props> = ({
  editingUser,
  isSubmitting,
  onCreate,
  onUpdate,
  onClose,
}) => {
  const isEditMode = Boolean(editingUser);
  const { methods } = useAuthorizedUserForm({ defaultValue: editingUser });

  const handleSubmit = (values: AuthorizedUserFormValues) => {
    const params = {
      firstName: values.firstName,
      lastName: values.lastName,
      relationshipType: values.relationshipType as AuthorizedUser['relationshipType'],
      birthOn: values.birthOn,
    };
    if (isEditMode) {
      onUpdate(params);
    } else {
      onCreate(params);
    }
  };

  return (
    <ModalWrapper
      visible
      onSetVisible={onClose}
      testId={isEditMode ? 'authorized-users_edit-modal' : 'authorized-users_add-modal'}
    >
      <Box
        direction="row"
        align="center"
        justify="center"
        pad={{ bottom: 'spacing12', horizontal: 'spacing24' }}
      >
        <Text size="2xl" color="textBody" weight="bold" textAlign="center" data-testid="authorized-users-modal_title">
          {isEditMode ? 'Edit Authorized User' : 'Add Authorized User'}
        </Text>
      </Box>

      <Box
        pad={{ horizontal: 'spacing24' }}
        margin={{ bottom: 'spacing16' }}
      >
        <Text size="14px" weight={400} color="textTitle" textAlign="center" data-testid="authorized-users-modal_description">
          Identify additional individuals who are allowed to be provided with account information when contacting call center.
        </Text>
      </Box>

      <Box pad={{ horizontal: 'spacing24' }}>
        <Box round="moduleRound" background="module" pad="spacing24">
          <Box
            round="container1Round"
            border={{ color: 'border2', size: '1px' }}
            background="canvas"
            pad="spacing24"
          >
            <FormProvider {...methods}>
              <AuthorizedUsersForm />
            </FormProvider>
          </Box>
        </Box>

        <Box direction="row" justify="end" gap="spacing12" pad={{ top: 'spacing24' }}>
          <AppButton
            type="button"
            buttonType="secondary"
            width="control"
            testId="authorized-users-modal_cancel"
            onClick={onClose}
          >
            Cancel
          </AppButton>
          <AppButton
            type="button"
            width="control"
            testId="authorized-users-modal_submit"
            onClick={methods.handleSubmit(handleSubmit)}
          >
            {isSubmitting ? (
              <Preloader testId="authorized-users-modal_submit-preloader" color="white" />
            ) : isEditMode ? (
              'Save'
            ) : (
              'Add'
            )}
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default AuthorizedUsersModal;
